import React from 'react';
import PropTypes from 'prop-types';
import { CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import WarningIcon from '@mui/icons-material/Warning';
import { getBreakTypeName } from './breakOptionsModalHelpers';
import { BREAK_ACTIONS } from '../../../../utils/constants/breakConstants';
import { useTimeFormatters } from '../../../../utils/Hooks/venue/useVenueTimeFormatters';
import Modal from '../../../Modal/Modal';
import {
  ModalTitle,
  BreakInfoText,
  WarningContainer,
  WarningText,
  ButtonContainer,
  SubmitButton,
  CancelButton,
  StyledIconButton,
  ModalContentWrapper,
  CloseButtonWrapper,
} from './BreakOptionsModal.styles';

const EndBreakConfirmationModal = ({
  open,
  onClose,
  legalEndTime,
  legalEndBreakAction,
  isLoading,
  onEndBreakSubmit,
}) => {
  const { formatTime } = useTimeFormatters(); // Use venue timezone formatters

  if (!open) {
    return null;
  }

  // Determine break type based on the action
  let breakType;
  try {
    if (legalEndBreakAction === BREAK_ACTIONS.END_REST) {
      breakType = 'REST_BREAK';
    } else if (legalEndBreakAction === BREAK_ACTIONS.END_MEAL) {
      breakType = 'MEAL_BREAK';
    } else {
      return null;
    }
  } catch (error) {
    console.error('Error determining break type:', error);
    return null;
  }

  let breakTypeName;
  try {
    breakTypeName = getBreakTypeName(breakType);
  } catch (error) {
    breakTypeName = 'Break';
  }

  let formattedLegalEndTime;
  try {
    formattedLegalEndTime = formatTime(legalEndTime);
  } catch (error) {
    formattedLegalEndTime = 'the scheduled time';
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      width={480}
      maxHeight="90vh"
      variant="default"
      showCloseButton={false}
    >
      <ModalContentWrapper>
        <CloseButtonWrapper>
          <StyledIconButton onClick={onClose} size="small" aria-label="close">
            <CloseIcon />
          </StyledIconButton>
        </CloseButtonWrapper>

        <ModalTitle variant="h6" component="h2">
          End Break Confirmation
        </ModalTitle>

        <BreakInfoText variant="body1">
          You are about to end your {breakTypeName.toLowerCase()} early.
        </BreakInfoText>

        <WarningContainer>
          <WarningIcon color="warning" />
          <WarningText>
            <strong>Warning: </strong>
            Ending this break now will cause a violation. We recommend you end your break after{' '}
            {formattedLegalEndTime}.
          </WarningText>
        </WarningContainer>

        <ButtonContainer>
          <SubmitButton variant="contained" onClick={onEndBreakSubmit} disabled={isLoading}>
            {isLoading ? <CircularProgress color="inherit" size={16} /> : 'End Break Anyway'}
          </SubmitButton>
          <CancelButton variant="outlined" onClick={onClose} disabled={isLoading}>
            Cancel
          </CancelButton>
        </ButtonContainer>
      </ModalContentWrapper>
    </Modal>
  );
};

EndBreakConfirmationModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  legalEndTime: PropTypes.instanceOf(Date),
  legalEndBreakAction: PropTypes.string,
  isLoading: PropTypes.bool,
  onEndBreakSubmit: PropTypes.func.isRequired,
};

export default EndBreakConfirmationModal;
